import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsAPI, interactionsAPI } from '../services/api';
import { Button, Avatar, StageBadge, type ContactStage } from '../components/ui';
import { TagBadge, type Tag } from '../components/ui/TagInput';
import { tagNameToSlug, getNextTagColor, type TagColorKey } from '../utils/tagColors';
import { ContactForm, LogInteractionModal, DraftMessageModal } from '../components/contacts';

// Helper to convert string tags to Tag objects with consistent colors
function stringTagsToTagObjects(tags: string[]): Tag[] {
  const usedColors: TagColorKey[] = [];
  return tags.map((name, _index) => {
    const slug = tagNameToSlug(name);
    const color = getNextTagColor(usedColors);
    usedColors.push(color);
    return {
      id: slug,
      name,
      slug,
      color,
    };
  });
}

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogInteraction, setShowLogInteraction] = useState(false);
  const [showDraftMessage, setShowDraftMessage] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState<any>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => contactsAPI.get(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => contactsAPI.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] });
      navigate('/contacts');
    },
  });

  const moveToBenchMutation = useMutation({
    mutationFn: () => contactsAPI.update(id!, { stage: 'bench' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] });
    },
  });

  const deleteInteractionMutation = useMutation({
    mutationFn: (interactionId: string) => interactionsAPI.delete(interactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
    },
  });

  const handleDeleteInteraction = (interactionId: string) => {
    deleteInteractionMutation.mutate(interactionId);
  };

  const contact = data?.data?.contact;
  const interactions = contact?.recent_interactions || [];

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">Contact not found.</p>
        <Link to="/contacts" className="text-ondeck-600 dark:text-ondeck-400 hover:underline mt-2 inline-block">
          Back to contacts
        </Link>
      </div>
    );
  }

  const contactName = `${contact.first_name} ${contact.last_name || ''}`.trim();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Link */}
      <Link to="/contacts" className="text-ondeck-600 dark:text-ondeck-400 hover:text-ondeck-700 dark:hover:text-ondeck-300 mb-4 inline-flex items-center gap-1">
        ← Back to contacts
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <Avatar name={contactName} size="xl" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{contactName}</h1>
            <div className="flex items-center gap-2 mt-1">
              <StageBadge stage={contact.stage as ContactStage} />
              {contact.company && (
                <span className="text-gray-600 dark:text-gray-400">• {contact.company}</span>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          {contact.phone && (
            <Button
              variant="primary"
              size="sm"
              className="sm:text-sm"
              onClick={() => window.open(`tel:${contact.phone}`, '_self')}
            >
              Call
            </Button>
          )}
          {contact.email && (
            <Button
              variant="outline"
              size="sm"
              className="sm:text-sm"
              onClick={() => window.open(`mailto:${contact.email}`, '_self')}
            >
              Email
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="sm:text-sm"
            onClick={() => setShowDraftMessage(true)}
          >
            Draft
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="sm:text-sm"
            onClick={() => setShowLogInteraction(true)}
          >
            Log
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI Summary */}
          {contact.ai_summary && (
            <div className="bg-ondeck-50 dark:bg-ondeck-900/30 border border-ondeck-200 dark:border-ondeck-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-ondeck-800 dark:text-ondeck-300 flex items-center gap-2">
                  <span>✨</span> AI Summary
                </h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Generated {contact.ai_summary_updated_at ? formatTimestamp(contact.ai_summary_updated_at) : 'recently'}
                </span>
              </div>
              <p className="text-ondeck-700 dark:text-ondeck-400">{contact.ai_summary}</p>
            </div>
          )}

          {/* Contact Info */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowEditModal(true)}>
                Edit
              </Button>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contact.email && (
                <div className="min-w-0">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Email</dt>
                  <dd className="text-gray-900 dark:text-gray-100 truncate">
                    <a href={`mailto:${contact.email}`} className="text-ondeck-600 dark:text-ondeck-400 hover:underline truncate block" title={contact.email}>
                      {contact.email}
                    </a>
                  </dd>
                </div>
              )}
              {contact.phone && (
                <div className="min-w-0">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    <a href={`tel:${contact.phone}`} className="text-ondeck-600 dark:text-ondeck-400 hover:underline">
                      {contact.phone}
                    </a>
                  </dd>
                </div>
              )}
              {contact.address_city && (
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Location</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    {[contact.address_city, contact.address_state].filter(Boolean).join(', ')}
                  </dd>
                </div>
              )}
              {contact.contact_type && (
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Type</dt>
                  <dd className="text-gray-900 dark:text-gray-100 capitalize">{contact.contact_type.replace('_', ' ')}</dd>
                </div>
              )}
              {contact.source && (
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Source</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{contact.source}</dd>
                </div>
              )}
              {contact.birthday && (
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Birthday</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{formatDate(contact.birthday)}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Recent Interactions - Chat Style */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Conversation</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowLogInteraction(true)}>
                + Log Interaction
              </Button>
            </div>

            {interactions.length > 0 ? (
              <div className="space-y-3">
                {interactions.map((interaction: any) => {
                  const isOutbound = interaction.direction === 'outbound';
                  const isPositive = interaction.ai_sentiment === 'positive' || interaction.outcome === 'positive';
                  const isNegative = interaction.ai_sentiment === 'negative' || interaction.outcome === 'negative';
                  return (
                    <div
                      key={interaction.id}
                      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} group`}
                    >
                      <div
                        className={`relative max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          isOutbound
                            ? 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500 dark:border-purple-400 text-gray-900 dark:text-white rounded-br-md'
                            : 'bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 text-gray-900 dark:text-white rounded-bl-md'
                        }`}
                      >
                        {/* Edit and Delete Buttons - show on hover */}
                        <div className={`absolute top-1 ${isOutbound ? 'left-1' : 'right-1'} opacity-0 group-hover:opacity-100 transition-opacity flex gap-1`}>
                          <button
                            onClick={() => setEditingInteraction(interaction)}
                            className="p-1 rounded-full hover:bg-blue-500 hover:text-white text-gray-500 dark:text-gray-400"
                            title="Edit interaction"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Delete this interaction? This action cannot be undone.')) {
                                handleDeleteInteraction(interaction.id);
                              }
                            }}
                            className="p-1 rounded-full hover:bg-red-500 hover:text-white text-gray-500 dark:text-gray-400"
                            title="Delete interaction"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        {/* Header with icon, type, and sentiment */}
                        <div className={`flex items-center gap-2 mb-1 ${isOutbound ? 'justify-end' : ''}`}>
                          <span className="text-sm">{getInteractionIcon(interaction.type)}</span>
                          <span className={`text-xs font-medium capitalize ${isOutbound ? 'text-purple-700 dark:text-purple-300' : 'text-red-700 dark:text-red-300'}`}>
                            {interaction.type}
                          </span>
                          {isPositive && <span className="text-sm">😊</span>}
                          {isNegative && <span className="text-sm">😞</span>}
                        </div>

                        {/* Subject */}
                        {interaction.subject && (
                          <p className="text-sm font-medium mb-1 text-gray-900 dark:text-white">
                            {interaction.subject}
                          </p>
                        )}

                        {/* Content/Snippet */}
                        {interaction.snippet && (
                          <p className={`text-sm ${isOutbound ? 'text-purple-800 dark:text-purple-200' : 'text-red-800 dark:text-red-200'}`}>
                            {interaction.snippet}
                          </p>
                        )}

                        {/* Timestamp */}
                        <p className={`text-xs mt-1.5 ${isOutbound ? 'text-purple-500 dark:text-purple-400 text-right' : 'text-red-500 dark:text-red-400'}`}>
                          {formatTimestamp(interaction.occurred_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 mb-3">No interactions logged yet.</p>
                <Button variant="outline" size="sm" onClick={() => setShowLogInteraction(true)}>
                  Log your first interaction
                </Button>
              </div>
            )}
          </div>

          {/* Notes */}
          {contact.notes && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h2>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{contact.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Quick Stats</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Last Contact</dt>
                <dd className="text-gray-900 dark:text-gray-100">
                  {contact.last_contact_at ? formatTimestamp(contact.last_contact_at) : 'Never'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Next Contact</dt>
                <dd className="text-gray-900 dark:text-gray-100">
                  {contact.next_contact_date || 'Not scheduled'}
                </dd>
              </div>
              {contact.ai_engagement_score && (
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Engagement Score</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{Math.round(contact.ai_engagement_score * 100)}%</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                <dd className="text-gray-900 dark:text-gray-100">{formatTimestamp(contact.created_at)}</dd>
              </div>
            </dl>
          </div>

          {/* Tags */}
          {contact.tags?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {stringTagsToTagObjects(contact.tags).map((tag: Tag) => (
                  <TagBadge key={tag.id} tag={tag} />
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
              >
                Edit Contact
              </button>
              {contact.stage !== 'bench' && (
                <button
                  onClick={() => {
                    if (confirm('Move this contact to the bench?')) {
                      moveToBenchMutation.mutate();
                    }
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                >
                  Move to Bench
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to archive this contact?')) {
                    deleteMutation.mutate();
                  }
                }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
              >
                Archive Contact
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ContactForm
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        contact={contact}
      />

      <LogInteractionModal
        open={showLogInteraction}
        onClose={() => setShowLogInteraction(false)}
        contactId={id!}
        contactName={contactName}
      />

      <LogInteractionModal
        open={!!editingInteraction}
        onClose={() => setEditingInteraction(null)}
        contactId={id!}
        contactName={contactName}
        interaction={editingInteraction}
      />

      <DraftMessageModal
        open={showDraftMessage}
        onClose={() => setShowDraftMessage(false)}
        contact={contact}
      />
    </div>
  );
}

function getInteractionIcon(type: string): string {
  const icons: Record<string, string> = {
    call: '📞',
    email: '✉️',
    sms: '💬',
    meeting: '📅',
    note: '📝',
    task: '✅',
  };
  return icons[type] || '📋';
}

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
